import { useEffect, useState } from 'react';

interface Confetti {
  id: number;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  color: string;
  velocity: {
    x: number;
    y: number;
  };
  rotationSpeed: number;
}

interface ConfettiSuccessProps {
  /** Nombre de confettis à afficher */
  count?: number;
  /** Durée de l'animation en millisecondes */
  duration?: number;
  /** Callback appelé à la fin de l'animation */
  onComplete?: () => void;
  /** Afficher ou non l'animation */
  show?: boolean;
}

const ConfettiSuccess = ({
  count = 50,
  duration = 3000,
  onComplete,
  show = true
}: ConfettiSuccessProps) => {
  const [confettis, setConfettis] = useState<Confetti[]>([]);
  const [isVisible, setIsVisible] = useState(show);

  const colors = [
    'bg-red-500',
    'bg-blue-500',
    'bg-green-500',
    'bg-yellow-500',
    'bg-purple-500',
    'bg-pink-500',
    'bg-orange-500',
    'bg-cyan-500',
  ];

  useEffect(() => {
    if (!show) {
      setIsVisible(false);
      return;
    }

    // Générer les confettis
    const newConfettis: Confetti[] = Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: -10,
      rotation: Math.random() * 360,
      scale: Math.random() * 0.5 + 0.5,
      color: colors[Math.floor(Math.random() * colors.length)],
      velocity: {
        x: (Math.random() - 0.5) * 3,
        y: Math.random() * 2 + 2,
      },
      rotationSpeed: (Math.random() - 0.5) * 10,
    }));

    setConfettis(newConfettis);
    setIsVisible(true);

    // Nettoyer après la durée spécifiée
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, duration);

    return () => clearTimeout(timer);
  }, [show, count, duration, onComplete]);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
      {confettis.map((confetti) => (
        <div
          key={confetti.id}
          className={`absolute w-3 h-3 ${confetti.color} rounded-sm`}
          style={{
            left: `${confetti.x}%`,
            top: `${confetti.y}%`,
            transform: `rotate(${confetti.rotation}deg) scale(${confetti.scale})`,
            animation: `confetti-fall ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards`,
            animationDelay: `${Math.random() * 200}ms`,
            '--final-x': `${confetti.x + confetti.velocity.x * 50}%`,
            '--final-y': '100%',
            '--rotation-speed': `${confetti.rotationSpeed * duration}deg`,
          } as React.CSSProperties}
        />
      ))}

      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(calc(100vh + 50px)) translateX(var(--final-x, 0)) rotate(var(--rotation-speed, 360deg));
            opacity: 0;
          }
        }
      `}</style>
    </div>
  );
};

export default ConfettiSuccess;
